import { useEffect, useMemo } from 'react';

import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/ui/select';
import { useAtom, useAtomValue } from 'jotai';
import { useHistory, useLocation } from 'react-router';

import { ImageType, type TImagetype } from '../../../@types/global';
import type { Visit } from '../../../@types/visits';
import {
  selectedUnifiedIdAtom,
  selectedVisitAtom,
} from '../../../app/appHeader/imageViewerMenu/atoms';
import { hasImageIdInAnnotatorHash } from '../../../app/appHeader/path-utils';
import { isExternalUserAtom } from '../../../atoms/atoms';
import {
  externalPmsSourceAtom,
  imageTimestampAtom,
  studyDateAtom,
} from '../../../atoms/externalAtoms';
import { useGetDateFNSLocale } from '../../../locales/helpers';
import { selectUnifiedIdAtom } from '../../../modules/patientList/atoms';
import { CheckButton } from '../../../modules/patientList/patientStrip/findings/CheckButton';
import { useGetPatientImageStatuses } from '../../../react-query/useGetPatientImageStatuses';
import { usePreferences } from '../../../react-query/usePreferences';
import routesConstants from '../../../routes/routes.constants';
import { useIsEmbedded } from '../../../utils/hooks';
import { useVisits } from '../../../utils/hooks/useVisits';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { parseAndFormatDate } from '../../../utils/parseDate';
import { useMapImageTypeToLabel } from '../../../utils/string';

interface VisitSelectorProps {
  patientId: string;
}

// Helper function to find the closest visit to a target date
const findClosestVisit = (
  visits: Visit[],
  targetDateString: string,
): Visit | undefined => {
  const targetDate = new Date(targetDateString);
  if (isNaN(targetDate.getTime())) {
    return undefined;
  }

  let closestVisit: Visit | undefined;
  let smallestDiff = Infinity;

  visits.forEach((visit) => {
    if (visit.visitDate) {
      const visitDateObj = new Date(visit.visitDate);
      const diff = Math.abs(visitDateObj.getTime() - targetDate.getTime());
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestVisit = visit;
      }
    }
  });

  return closestVisit;
};

export const VisitSelector = ({ patientId }: VisitSelectorProps) => {
  const history = useHistory();
  const isEmbedded = useIsEmbedded();
  const { t } = useLingui();

  const [selectedVisit, setSelectedVisit] = useAtom(selectedVisitAtom);
  const [isExternalUser] = useAtom(isExternalUserAtom);
  const [selectUnifiedId] = useAtom(selectUnifiedIdAtom);
  const [imageTimestamp, setImageTimestamp] = useAtom(imageTimestampAtom);
  const [studyDate, setStudyDate] = useAtom(studyDateAtom);
  const externalPmsSource = useAtomValue(externalPmsSourceAtom);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const { trackEvent } = useTrackEvent();
  const { data: prefs } = usePreferences();
  const isHistoricalTriggerEnabled = prefs?.historicalTriggerEnabled;
  const mapImageTypeToLabel = useMapImageTypeToLabel();
  const dateFNSLocale = useGetDateFNSLocale();
  const [, setSelectedUnifiedId] = useAtom(selectedUnifiedIdAtom);

  const { allVisits, ddaiVisits, connectorVisits } = useVisits(patientId);

  // Get image statuses for all visits only if preference is enabled
  const { data: imageStatusData } = useGetPatientImageStatuses(
    prefs?.allowRerunAnalysesFromBridgeLinkEvent
      ? { videaPatientId: patientId }
      : undefined,
  );

  useEffect(() => {
    let defaultVisitId = '';

    const visitId = params.get('visit-id');

    if (visitId) {
      const defaultId = allVisits.find((visit) => {
        if (!!visit && 'id' in visit) {
          return !!visit.id && visit.id.toString() === visitId;
        }
      });
      if (defaultId && 'id' in defaultId) {
        defaultVisitId = String(defaultId.id);
      } else {
        defaultVisitId = '';
      }
    } else if (
      selectedVisit &&
      allVisits.find((visit) => {
        if (!!visit && 'id' in visit) {
          return !!visit.id && visit.id.toString() === selectedVisit;
        }
      })
    ) {
      return;
    }

    // If timestamp is provided, find the closest visit to that timestamp
    if (!defaultVisitId && imageTimestamp && allVisits.length > 0) {
      const closestVisit = findClosestVisit(allVisits, imageTimestamp);
      if (closestVisit?.id) {
        defaultVisitId = closestVisit.id.toString();
      }
      // Clear the timestamp after using it
      setImageTimestamp('');
    }

    // If studyDate is provided (Apteryx flow), find the closest visit to that date
    if (!defaultVisitId && studyDate && allVisits.length > 0) {
      const closestVisit = findClosestVisit(allVisits, studyDate);
      if (closestVisit?.id) {
        defaultVisitId = closestVisit.id.toString();
      }
      // Clear the studyDate after using it
      setStudyDate('');
    }

    // If no default visit and external user, default to ddai visit,
    // otherwise connector visit
    if (
      isExternalUser &&
      !defaultVisitId &&
      ddaiVisits &&
      ddaiVisits[0]?.id &&
      externalPmsSource !== 'Apteryx'
    ) {
      defaultVisitId = ddaiVisits[0].id.toString();
    } else if (!defaultVisitId && connectorVisits && connectorVisits[0]?.id) {
      defaultVisitId = connectorVisits[0].id.toString();
    } else if (!defaultVisitId && allVisits && allVisits[0]?.id) {
      defaultVisitId = allVisits[0].id.toString();
    }
    const defaultVisit = allVisits.find(
      (visit) => visit.id && String(visit.id) === defaultVisitId,
    );

    !!defaultVisitId && setSelectedVisit(defaultVisitId.toString());
    params.delete('visit-id');

    const unifiedId = params.get('unified-id');
    !!unifiedId && setSelectedUnifiedId(unifiedId);
    params.delete('unified-id');

    // Check if there's an image ID in the URL hash to avoid race conditions
    // This component does not have access to useParams, so we check the hash via path-utils
    const hasImageIdInUrl = hasImageIdInAnnotatorHash();

    // Don't redirect if there's an active image ID in the URL
    // This prevents removing the image ID parameter when landing on a specific image
    if (hasImageIdInUrl) {
      return;
    }

    history.replace({
      search: params.toString(),
      pathname: `${isEmbedded ? '/embedded' : ''}${routesConstants.ANNOTATOR}/${
        defaultVisit?.patientVideaId || patientId
      }`,
    });

    // We only want to auto select a visit when first landing on the Image Viewer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ddaiVisits,
    connectorVisits,
    setSelectedVisit,
    setSelectedUnifiedId,
    imageTimestamp,
    setImageTimestamp,
    studyDate,
    setStudyDate,
  ]);

  const handleVisitChange = (value: string | null) => {
    setSelectedVisit(value);
    trackEvent(mixpanelEvents.switchVisits, {
      currentVisitId: value,
      selectedVisitId: selectedVisit,
      source: 'visitListSelect',
    });

    const newVisit = allVisits.find(
      (visit) => visit.id && String(visit.id) === value,
    );

    history.push({
      pathname: `${isEmbedded ? '/embedded' : ''}${routesConstants.ANNOTATOR}/${
        newVisit?.patientVideaId || patientId
      }`,
      search: history.location.search,
    });
  };

  const visitOptions = useMemo(() => {
    const createOptions = (visitList: Visit[]) =>
      visitList.map((visit) => {
        const date = new Date(visit.visitDate || '');

        // Check if this visit has pending analyses
        const hasPendingAnalyses = (() => {
          if (!imageStatusData?.imageStatuses) return false;

          // First try to match by estimatedVisitId if available
          const visitImageStatuses = imageStatusData.imageStatuses.filter(
            (status) => {
              if (status.estimatedVisitId && visit.id) {
                return status.estimatedVisitId === Number(visit.id);
              }
              // Fall back to date matching
              const visitDateString = date.toISOString().split('T')[0];
              return status.acquiredDate === visitDateString;
            },
          );

          const totalPending = visitImageStatuses.reduce(
            (sum, status) => sum + status.pending,
            0,
          );

          return totalPending > 0;
        })();

        return (
          <SelectItem
            key={visit.id}
            value={visit.id?.toString() || ''}
            className="vai-flex vai-items-center vai-gap-3 vai-px-3 vai-py-2"
          >
            <span className="vai-text-sm vai-font-medium">
              {parseAndFormatDate(date.toISOString(), 'PP', dateFNSLocale)}
            </span>
            {hasPendingAnalyses && (
              <FontAwesomeIcon
                icon={faSpinner}
                className="vai-h-3 vai-w-3 vai-text-zinc-300 vai-animate-spin vai-ml-2 vai-mr-2"
              />
            )}
            {visit.source === 'DDAI' ? (
              <span className="vai-inline-block vai-px-2 vai-py-1 vai-bg-transparent vai-border vai-border-gray-400 vai-rounded vai-text-gray-400 vai-text-xs vai-font-normal vai-ml-2">
                <Trans>DDAI</Trans>
              </span>
            ) : null}
            {visit.imageTypeCounts?.map(
              (typeCount: {
                type: TImagetype;
                count: string | number | bigint;
              }) => {
                const { type, count } = typeCount;
                if (type === ImageType.OTHER) return null;
                const countNumber =
                  typeof count === 'number' ? count : Number(count);
                return (
                  <span
                    key={`${mapImageTypeToLabel[type]}-${countNumber}`}
                    className="vai-inline-block vai-px-2 vai-py-1 vai-bg-gray-600 vai-rounded vai-text-white vai-text-xs vai-font-normal vai-ml-1"
                  >
                    {mapImageTypeToLabel[type]}
                    <span className="vai-font-bold vai-ml-1">
                      {countNumber}
                    </span>
                  </span>
                );
              },
            )}
          </SelectItem>
        );
      });

    if (isExternalUser) {
      return createOptions(ddaiVisits || []);
    }

    if (prefs?.enableMultiSource) {
      return createOptions(allVisits || []);
    }

    const ddaiOptions = createOptions(ddaiVisits || []);
    const connectorOptions = createOptions(connectorVisits || []);
    return [...connectorOptions, ...ddaiOptions];
  }, [
    allVisits,
    ddaiVisits,
    connectorVisits,
    isExternalUser,
    prefs?.enableMultiSource,
    mapImageTypeToLabel,
    dateFNSLocale,
    imageStatusData,
  ]);

  // Get selected visit display value
  const getSelectedVisitDisplayValue = () => {
    if (!selectedVisit) return t`Select visit`;

    const selectedVisitData = allVisits.find(
      (visit) => visit.id?.toString() === selectedVisit,
    );

    if (selectedVisitData) {
      const date = new Date(selectedVisitData.visitDate || '');
      return parseAndFormatDate(date.toISOString(), 'PP', dateFNSLocale);
    }

    return t`Select visit`;
  };

  return (
    <div className="vai-flex vai-items-center vai-gap-0">
      {!isEmbedded && (
        <span className="vai-text-white vai-text-sm vai-font-medium vai-mr-2 vai-whitespace-nowrap">
          <Trans>Appt:</Trans>
        </span>
      )}
      <Select value={selectedVisit || ''} onValueChange={handleVisitChange}>
        <SelectTrigger className="vai-w-64 !vai-bg-[#09090B] vai-text-white vai-border-none">
          <SelectValue>{getSelectedVisitDisplayValue()}</SelectValue>
        </SelectTrigger>
        <SelectContent className="!vai-bg-[#09090B] !vai-border-gray-700 vai-text-white">
          {visitOptions}
          {isHistoricalTriggerEnabled && selectUnifiedId && patientId && (
            <CheckButton unifiedId={selectUnifiedId} patientId={patientId} />
          )}
        </SelectContent>
      </Select>
    </div>
  );
};
